import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Plus, Trash2, BookOpen, Search, ChevronDown, ChevronUp, Save } from 'lucide-react';
import { useRecipes, useItems, useUpsertRecipe } from '../hooks/useInventory';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../utils/api';

// ── Types ──────────────────────────────────────────────────────────────────────
interface IngredientRow {
  itemId: string;
  qty: number;
  unit: string;
  notes: string;
}

interface PosProduct {
  id: string | number;
  name: string;
  price: number;
  category?: { name?: string } | string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────
const UNITS = ['g', 'kg', 'ml', 'litros', 'unidades', 'porciones', 'cucharadas', 'tazas'];

function IngredientRowForm({
  row, index, items, onChange, onRemove,
}: {
  row: IngredientRow;
  index: number;
  items: { id: string; name: string; unit: string }[];
  onChange: (index: number, field: keyof IngredientRow, value: string | number) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-2 border-b border-divider/40 last:border-0">
      <div className="col-span-4">
        <select
          value={row.itemId}
          onChange={(e) => onChange(index, 'itemId', e.target.value)}
          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Ingrediente"
        >
          <option value="">-- Ingrediente --</option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>{i.name} ({i.unit})</option>
          ))}
        </select>
      </div>
      <div className="col-span-2">
        <input
          type="number"
          min={0}
          step="any"
          value={row.qty}
          onChange={(e) => onChange(index, 'qty', parseFloat(e.target.value) || 0)}
          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Cant."
          aria-label="Cantidad"
        />
      </div>
      <div className="col-span-2">
        <select
          value={row.unit}
          onChange={(e) => onChange(index, 'unit', e.target.value)}
          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Unidad"
        >
          {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
      </div>
      <div className="col-span-3">
        <input
          type="text"
          value={row.notes}
          onChange={(e) => onChange(index, 'notes', e.target.value)}
          placeholder="Notas (opcional)"
          className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          aria-label="Notas"
        />
      </div>
      <div className="col-span-1 flex justify-center">
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="flex h-8 w-8 items-center justify-center rounded-full text-red-500 hover:bg-red-50 transition"
          aria-label="Eliminar ingrediente"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
const RecipesPage: React.FC = () => {
  const { data: recipes, isLoading: loadingRecipes } = useRecipes();
  const { data: itemsPage } = useItems({ limit: 200 });
  const upsert = useUpsertRecipe();

  // Load POS products from the main API
  const { data: posProducts } = useQuery<PosProduct[]>({
    queryKey: ['products', 'forRecipes'],
    queryFn: async () => {
      const res = await api.get('/products');
      return Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
    },
    staleTime: 60_000,
  });

  const inventoryItems = (itemsPage?.data ?? []).map((i) => ({
    id: i.id,
    name: i.name,
    unit: i.unit ?? '',
  }));

  // ── Local state ────────────────────────────────────────────────────────────
  const [selectedProductId, setSelectedProductId] = useState('');
  const [recipeName, setRecipeName] = useState('');
  const [ingredients, setIngredients] = useState<IngredientRow[]>([
    { itemId: '', qty: 1, unit: 'g', notes: '' },
  ]);
  const [q, setQ] = useState('');
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);

  const filteredRecipes = (recipes ?? []).filter((r) =>
    r.name?.toLowerCase().includes(q.toLowerCase()) ||
    r.productId.toLowerCase().includes(q.toLowerCase())
  );

  const handleIngredientChange = (index: number, field: keyof IngredientRow, value: string | number) => {
    setIngredients((prev) => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const addIngredient = () => {
    setIngredients((prev) => [...prev, { itemId: '', qty: 1, unit: 'g', notes: '' }]);
  };

  const removeIngredient = (index: number) => {
    setIngredients((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (!selectedProductId) { toast.error('Selecciona un producto'); return; }
    const valid = ingredients.filter((i) => i.itemId && i.qty > 0);
    if (valid.length === 0) { toast.error('Agrega al menos un ingrediente válido'); return; }

    upsert.mutate(
      {
        productId: String(selectedProductId),
        name: recipeName || undefined,
        ingredients: valid.map((i) => ({
          itemId: i.itemId,
          qty: i.qty,
          unit: i.unit,
          notes: i.notes || undefined,
        })),
      },
      {
        onSuccess: () => {
          toast.success('Receta guardada correctamente');
          setSelectedProductId('');
          setRecipeName('');
          setIngredients([{ itemId: '', qty: 1, unit: 'g', notes: '' }]);
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message ?? 'Error al guardar la receta');
        },
      }
    );
  };

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-8">

        {/* Header */}
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-muted">Inventario</p>
          <h1 className="text-3xl font-bold text-[color:var(--color-text)] flex items-center gap-3">
            <BookOpen size={28} className="text-primary" /> Recetas de Productos
          </h1>
          <p className="mt-1 text-sm text-muted">
            Define los ingredientes de cada producto del POS para que el stock se descuente automáticamente al cobrar.
          </p>
        </div>

        {/* Form */}
        <div className="rounded-2xl border border-divider bg-surface p-6 shadow-sm space-y-5">
          <h2 className="text-base font-bold text-[color:var(--color-text)]">Nueva receta / Actualizar</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="product-select">
                Producto del POS
              </label>
              <select
                id="product-select"
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">-- Selecciona un producto --</option>
                {(posProducts ?? []).map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.name} {typeof p.category === 'object' ? `(${p.category?.name})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted" htmlFor="recipe-name">
                Nombre de la receta (opcional)
              </label>
              <input
                id="recipe-name"
                type="text"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder="Ej: Banana Split estándar"
                className="w-full rounded-xl border border-divider bg-surface-muted px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {/* Header row */}
          <div className="grid grid-cols-12 gap-2 px-1 text-[10px] font-bold uppercase text-muted">
            <div className="col-span-4">Ingrediente</div>
            <div className="col-span-2">Cantidad</div>
            <div className="col-span-2">Unidad</div>
            <div className="col-span-3">Notas</div>
            <div className="col-span-1" />
          </div>

          <div>
            {ingredients.map((row, i) => (
              <IngredientRowForm
                key={i}
                row={row}
                index={i}
                items={inventoryItems}
                onChange={handleIngredientChange}
                onRemove={removeIngredient}
              />
            ))}
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={addIngredient}
              className="inline-flex items-center gap-2 rounded-full border border-divider px-4 py-2 text-sm font-semibold transition hover:bg-surface-muted"
            >
              <Plus size={14} /> Agregar ingrediente
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={upsert.isPending}
              className="ml-auto inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2 text-sm font-bold text-white shadow transition hover:opacity-90 disabled:opacity-60"
            >
              <Save size={14} />
              {upsert.isPending ? 'Guardando...' : 'Guardar receta'}
            </button>
          </div>
        </div>

        {/* Existing recipes list */}
        <div className="rounded-2xl border border-divider bg-surface p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-bold text-[color:var(--color-text)]">Recetas existentes</h2>
            <div className="relative ml-auto w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="text"
                placeholder="Buscar..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full rounded-full border border-divider bg-surface-muted pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>

          {loadingRecipes ? (
            <div className="space-y-3">{[...Array(4)].map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-surface-muted" />
            ))}</div>
          ) : filteredRecipes.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">
              No hay recetas creadas aún. ¡Crea la primera!
            </p>
          ) : (
            <div className="space-y-2">
              {filteredRecipes.map((recipe) => {
                const isOpen = expandedRecipe === recipe._id;
                const product = (posProducts ?? []).find((p) => String(p.id) === recipe.productId);
                return (
                  <div key={recipe._id} className="rounded-2xl border border-divider overflow-hidden">
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-muted transition"
                      onClick={() => setExpandedRecipe(isOpen ? null : recipe._id)}
                    >
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-[color:var(--color-text)]">
                          {recipe.name ?? product?.name ?? recipe.productId}
                        </p>
                        <p className="text-xs text-muted">{recipe.ingredients.length} ingrediente(s)</p>
                      </div>
                      <button
                        type="button"
                        className="text-xs text-primary font-semibold mr-2 hover:underline"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProductId(recipe.productId);
                          setRecipeName(recipe.name ?? '');
                          setIngredients(recipe.ingredients.map((ing) => ({
                            itemId: (ing.item as any)?.id ?? (ing.item as any)?._id ?? String(ing.item),
                            qty: ing.qty,
                            unit: ing.unit,
                            notes: ing.notes ?? '',
                          })));
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                      >
                        Editar
                      </button>
                      {isOpen ? <ChevronUp size={16} className="text-muted" /> : <ChevronDown size={16} className="text-muted" />}
                    </button>
                    {isOpen && (
                      <div className="border-t border-divider bg-surface-muted px-4 py-3">
                        <div className="space-y-1">
                          {recipe.ingredients.map((ing, i) => (
                            <div key={i} className="flex items-center justify-between text-sm">
                              <span className="font-medium text-[color:var(--color-text)]">
                                {(ing.item as any)?.name ?? 'Ingrediente'}
                              </span>
                              <span className="text-muted">
                                {ing.qty} {ing.unit}
                                {ing.notes ? ` · ${ing.notes}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipesPage;
